import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Slide,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Demo audit log data - top 10 rows
const demoAuditLogs = [
    {
        id: 1,
        societyId: '526534',
        societyName: 'Sunrise Apartments',
        action: 'Created',
        description: 'Society was created successfully',
        performedBy: 'Admin User',
        performedAt: '2026-01-23T10:30:00',
        ipAddress: '192.168.1.100',
        status: 'Success',
    },
    {
        id: 2,
        societyId: '526535',
        societyName: 'Green Valley Society',
        action: 'Updated',
        description: 'Email notifications enabled',
        performedBy: 'Admin User',
        performedAt: '2026-01-22T14:15:00',
        ipAddress: '192.168.1.100',
        status: 'Success',
    },
    {
        id: 3,
        societyId: '526536',
        societyName: 'Palm Heights',
        action: 'Login',
        description: 'User logged in successfully',
        performedBy: 'Society Admin',
        performedAt: '2026-01-22T09:00:00',
        ipAddress: '192.168.1.105',
        status: 'Success',
    },
    {
        id: 4,
        societyId: '526534',
        societyName: 'Sunrise Apartments',
        action: 'Updated',
        description: 'Added 5 new members to the society',
        performedBy: 'Society Admin',
        performedAt: '2026-01-21T16:45:00',
        ipAddress: '192.168.1.105',
        status: 'Success',
    },
    {
        id: 5,
        societyId: '526537',
        societyName: 'Royal Residency',
        action: 'Deleted',
        description: 'Invoice #INV-2024-001 was deleted',
        performedBy: 'Admin User',
        performedAt: '2026-01-21T11:20:00',
        ipAddress: '192.168.1.100',
        status: 'Warning',
    },
    {
        id: 6,
        societyId: '526535',
        societyName: 'Green Valley Society',
        action: 'Updated',
        description: 'Society profile information updated',
        performedBy: 'Society Admin',
        performedAt: '2026-01-20T15:30:00',
        ipAddress: '192.168.1.105',
        status: 'Success',
    },
    {
        id: 7,
        societyId: '526538',
        societyName: 'City Tower Complex',
        action: 'Failed',
        description: 'Payment processing failed - insufficient funds',
        performedBy: 'System',
        performedAt: '2026-01-20T10:00:00',
        ipAddress: '192.168.1.1',
        status: 'Error',
    },
    {
        id: 8,
        societyId: '526536',
        societyName: 'Palm Heights',
        action: 'Created',
        description: 'New notice published: Monthly Meeting',
        performedBy: 'Society Admin',
        performedAt: '2026-01-19T14:00:00',
        ipAddress: '192.168.1.105',
        status: 'Success',
    },
    {
        id: 9,
        societyId: '526539',
        societyName: 'Lakeside Gardens',
        action: 'Updated',
        description: 'Billing cycle changed to monthly',
        performedBy: 'Admin User',
        performedAt: '2026-01-18T09:30:00',
        ipAddress: '192.168.1.100',
        status: 'Success',
    },
    {
        id: 10,
        societyId: '526534',
        societyName: 'Sunrise Apartments',
        action: 'Logout',
        description: 'User session ended',
        performedBy: 'Society Admin',
        performedAt: '2026-01-17T18:00:00',
        ipAddress: '192.168.1.105',
        status: 'Success',
    },
];

const AuditLogModal = ({ open, handleClose, society }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return formatIstDate(date);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Success':
                return { bg: 'bg-success-focus', text: 'text-success-main' };
            case 'Warning':
                return { bg: 'bg-warning-focus', text: 'text-warning-main' };
            case 'Error':
                return { bg: 'bg-danger-focus', text: 'text-danger-main' };
            default:
                return { bg: 'bg-primary-light', text: 'text-primary-600' };
        }
    };

    const getActionIcon = (action) => {
        switch (action) {
            case 'Created':
                return 'solar:add-circle-bold';
            case 'Updated':
                return 'solar:pen-bold';
            case 'Deleted':
                return 'solar:trash-bin-trash-bold';
            case 'Login':
                return 'solar:login-bold';
            case 'Logout':
                return 'solar:logout-bold';
            case 'Failed':
                return 'solar:close-circle-bold';
            default:
                return 'solar:document-text-bold';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />
                <DialogTitle sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Icon icon="solar:clipboard-list-bold" color="white" width={40} />
                            <div>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                                    Audit Log
                                </Typography>
                                <p className='text-sm text-white mb-0'>
                                    {society?.name} (ID: {society?.id})
                                </p>
                            </div>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
            </Box>

            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col" className="text-sm fw-semibold">#</th>
                                    <th scope="col" className="text-sm fw-semibold">Community ID</th>
                                    <th scope="col" className="text-sm fw-semibold">Community Name</th>
                                    <th scope="col" className="text-sm fw-semibold">Action</th>
                                    <th scope="col" className="text-sm fw-semibold">Description</th>
                                    <th scope="col" className="text-sm fw-semibold">Performed By</th>
                                    <th scope="col" className="text-sm fw-semibold">Date & Time</th>
                                    <th scope="col" className="text-sm fw-semibold">IP Address</th>
                                    <th scope="col" className="text-sm fw-semibold">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {demoAuditLogs.map((log, index) => {
                                    const statusColor = getStatusColor(log.status);
                                    return (
                                        <tr key={log.id}>
                                            <td className="text-sm">
                                                <span className="text-secondary-light fw-medium">
                                                    {String(index + 1).padStart(2, '0')}
                                                </span>
                                            </td>
                                            <td>
                                                <code className="text-xs bg-primary-light text-primary-600 px-8 py-4 radius-4 fw-semibold">
                                                    {log.societyId}
                                                </code>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="w-28-px h-28-px bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center">
                                                        <Icon icon="solar:buildings-bold" width={14} />
                                                    </span>
                                                    <span className="text-sm fw-medium">{log.societyName}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className={`w-28-px h-28-px ${statusColor.bg} ${statusColor.text} rounded-circle d-inline-flex align-items-center justify-content-center`}>
                                                        <Icon icon={getActionIcon(log.action)} width={14} />
                                                    </span>
                                                    <span className="text-sm fw-medium">{log.action}</span>
                                                </div>
                                            </td>
                                            <td className="text-sm text-secondary-light" style={{ maxWidth: '250px' }}>
                                                {log.description}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="w-28-px h-28-px bg-neutral-100 rounded-circle d-inline-flex align-items-center justify-content-center">
                                                        <Icon icon="solar:user-bold" className="text-secondary-light" width={14} />
                                                    </span>
                                                    <span className="text-sm fw-medium">{log.performedBy}</span>
                                                </div>
                                            </td>
                                            <td className="text-sm text-secondary-light">
                                                {formatDate(log.performedAt)}
                                            </td>
                                            <td>
                                                <code className="text-xs bg-neutral-100 px-8 py-4 radius-4">
                                                    {log.ipAddress}
                                                </code>
                                            </td>
                                            <td>
                                                <span className={`badge ${statusColor.bg} ${statusColor.text} px-12 py-6 radius-4 text-xs fw-semibold`}>
                                                    {log.status}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="d-flex justify-content-between align-items-center px-24 py-16 border-top">
                        <span className="text-sm text-secondary-light">
                            Showing <span className="fw-semibold text-primary-600">10</span> of <span className="fw-semibold">156</span> entries
                        </span>
                        <button
                            type="button"
                            className="btn btn-outline-primary radius-8 px-16 py-8 text-sm"
                            onClick={handleClose}
                        >
                            <Icon icon="solar:arrow-left-outline" className="me-6" />
                            Close
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AuditLogModal;
